import { colors } from '@lmc/common/constants'
import type { AboutMe } from '@lmc/ts-common'
import type { FC } from 'react'
import './MultiTextArea.less'
import styled from 'styled-components'
import Popover from '../../../dataDisplay/Popover'
import { FormCard } from '../../../dataEntry/FormCard'
import Icon from '../../../general/Icon'
import { IconType } from '../../../general/Icon/iconTypes'
import Text from '../../atoms/Text'
import InputText from '../InputText'
import { RichTextAreaField } from './RichTextAreaField'
import { hasContent } from './utils'

type MultiTextAreaField = {
  source: string
  value: string
  element: 'input' | 'textarea'
  editMode: boolean
  label: string
  localisation: { description: string; writtenBy: string }
}

export type MultiTextAreaProps = {
  heading: string
  headingInfo: string
  tooltip: string
  fields?: MultiTextAreaField[]
  printPDF?: boolean
  formChange?: AboutMe
  printPDFPageBreak?: boolean
  onRichTextChange?: (source: string, html: string) => void
}

const MultiTextArea: FC<MultiTextAreaProps> = ({
  heading,
  headingInfo,
  fields,
  tooltip,
  printPDF,
  formChange,
  printPDFPageBreak = true,
  onRichTextChange
}) => {
  const headerNode = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text
          as={!printPDF ? 'h4' : 'h3'}
          style={{ fontWeight: 400, wordBreak: 'break-word' as any }}
        >
          {heading}
        </Text>
        {!printPDF && (
          <Popover
            placement="bottom"
            content={
              <div style={{ width: '50vw', maxWidth: 606 }}>{tooltip}</div>
            }
          >
            <div style={{ marginRight: 14 }}>
              <Icon
                customIcon={IconType.Question}
                style={{ transform: 'translateY(-1px)' }}
              />
            </div>
          </Popover>
        )}
      </div>
      <HeadingInfo>{headingInfo}</HeadingInfo>
    </div>
  )

  return (
    <Wrapper className={printPDFPageBreak ? 'page-break-after' : ''}>
      <FormCard title={headerNode} level={1} contentGap={20}>
        {fields?.map((field, index) => {
          const label =
            index === 0
              ? field.localisation.description
              : field.localisation.writtenBy

          const isEditMode = field.editMode

          if (field.element === 'textarea') {
            return (
              <RichTextAreaField
                key={field.source}
                source={field.source}
                value={field.value}
                editMode={isEditMode}
                printPDF={printPDF}
                label={label}
                onChangeHtml={onRichTextChange ?? (() => {})}
              />
            )
          }

          const parentField = field.source.replace('WrittenBy', '')
          const parentValue = formChange?.[parentField as keyof AboutMe] as
            | string
            | undefined
          const required = hasContent(parentValue)

          return !isEditMode ? (
            <div key={field.source}>
              <FieldLabel as={!printPDF ? 'p3' : 'h5'}>{label}</FieldLabel>
              <InputValue>{field.value}</InputValue>
            </div>
          ) : (
            <InputText
              key={field.source}
              label={label}
              type={field.source}
              errorMessage={`${heading} ${label} is required!`}
              required={required}
              element={field.element}
              placeholder=" "
              style={{ cursor: 'text' }}
              autoSize
            />
          )
        })}
      </FormCard>
    </Wrapper>
  )
}

const FieldLabel = styled(Text)`
  display: block;
  margin-top: 5px;
  color: ${colors.darkgrey};
`

const Wrapper = styled.div`
  margin-top: 24px;

  .ant-divider-horizontal {
    margin: 12px 0 20px;
  }
`

const InputValue = styled.p`
  font-size: 16px;
  margin: 0;
  color: ${colors.black};
`

const HeadingInfo = styled.div`
  color: ${colors.darkgrey};
  margin-right: 20px;
  transform: translateY(-4px);
`

export default MultiTextArea
